import { isFeatureAvailable } from 'cadenza/features';
import { getProjectId } from 'cadenza/store/cadenza.state';
import type { EnrichmentDto } from 'cadenza/workbook/enrichment/model/enrichment-dto';
import { getObjecttypeById } from 'cadenza/workbook/workbook.state';
import { getStore } from 'cadenza/store/cadenza-store';
import {
  areRulesSatisfiedForRerunningAnEnrichment,
  getAsyncEnrichmentRuleCheckerOptions,
} from 'cadenza/workbook/workbook-panel/databrowser/enrichment-rule-checker';
import { getObjectTypeDetails } from 'cadenza/api-client/objecttypes-api';

interface EnrichmentDetailsContextActionsRulesCheckedResult {
  isUserAllowedToChangeGroupingOrUngrouping: boolean;
  isUserAllowedToRerun: boolean;
  isUserAllowedToDelete: boolean;
  isUserAllowedToRelease: boolean;
}

export async function getEnrichmentDetailsContextActionsRulesCheckedResult(
  enrichment: EnrichmentDto,
  isMissingEnrichedObjecttype: boolean,
): Promise<EnrichmentDetailsContextActionsRulesCheckedResult> {
  const isUserAllowedToChangeGroupingOrUngrouping =
    isUserAllowedToChangeGroupingOrUngroupingOfEnrichmentAttributes(enrichment);
  const enrichmentRuleCheckerOptions = await getAsyncEnrichmentRuleCheckerOptions(enrichment.repositoryName);
  const isEnrichmentRerunnable = enrichment.enrichmentRerunnableStatus.value !== 'not-rerunnable';
  const isUserAllowedToRerun =
    isEnrichmentRerunnable && areRulesSatisfiedForRerunningAnEnrichment(enrichmentRuleCheckerOptions);
  const isUserAllowedToDelete = await isUserAllowedToDeleteTheEnrichment(enrichment, isMissingEnrichedObjecttype);
  const isUserAllowedToRelease = isUserAllowedToReleaseTheEnrichment(enrichment);
  return {
    isUserAllowedToChangeGroupingOrUngrouping,
    isUserAllowedToRerun,
    isUserAllowedToDelete,
    isUserAllowedToRelease,
  };
}

/**
 * A user can change the state of grouping/ungrouping if he has the 'write' privilege on the enrichment objecttype
 */
function isUserAllowedToChangeGroupingOrUngroupingOfEnrichmentAttributes(enrichment: EnrichmentDto): boolean {
  const enrichmentObjecttype = getObjecttypeById(getStore().getState(), enrichment.objecttypeId);
  return enrichmentObjecttype.currentUserPrivileges.includes('write');
}

/**
 * A user can release an enrichmentObjcttype if the feature 'WORKBOOK_TRANSFER_OBJECTTYPE_TO_REPOSITORY' is available
 * when releasing to repository (not to project), and he has the 'write' privilege on the enrichment objecttype
 * (which implicitely includes the 'modifyContent' privilege on the repository where the enrichment objecttype and the enriched
 * objecttype are saved to, which is the relevant privilege here)
 */
function isUserAllowedToReleaseTheEnrichment(enrichment: EnrichmentDto): boolean {
  const hasSystemPrivilegeToRelease =
    getProjectId(getStore().getState()) ? true : isFeatureAvailable('WORKBOOK_TRANSFER_OBJECTTYPE_TO_REPOSITORY');
  const enrichmentObjecttype = getObjecttypeById(getStore().getState(), enrichment.objecttypeId);
  return hasSystemPrivilegeToRelease && enrichmentObjecttype.currentUserPrivileges.includes('write');
}

/**
 * Scenario: If the user is currently seeing a workbook and wants to delete an enrichment objecttype there,
 * regardless of the location of workbook and enrichmentObjecttype (same or different repositories),
 * regardless of publishing enabled in one of the repositories, both of the repositories or none of the repositories
 * he can do this
 * IF he has the write privilege on the enrichmentObjecttype
 * AND the visible privilege on the enrichedObjecttype
 */
async function isUserAllowedToDeleteTheEnrichment(
  enrichmentDto: EnrichmentDto,
  isMissingEnrichedObjecttype: boolean,
): Promise<boolean> {
  if (isMissingEnrichedObjecttype) {
    const enrichedObjecttype = await getObjectTypeDetails(enrichmentDto.enrichedObjecttypeId);
    if (!enrichedObjecttype.currentUserPrivileges.includes('visible')) {
      return false;
    }
  } else {
    const enrichedObjecttype = getObjecttypeById(getStore().getState(), enrichmentDto.enrichedObjecttypeId);
    if (!enrichedObjecttype.currentUserPrivileges.includes('visible')) {
      return false;
    }
  }
  const enrichmentObjecttype = getObjecttypeById(getStore().getState(), enrichmentDto.objecttypeId);
  return enrichmentObjecttype.currentUserPrivileges.includes('write');
}
